import FuseCarousel from '@tvg/fuse-design/carousel';
import useConfigContext from 'client/context/config';
import {
  useCriteoLoadEvent,
  useCriteoViewEvent,
} from 'client/hooks/criteoEvents';
import createRecsWindowObj from 'client/utils/createRecsWindowObj';
import isApp from 'client/utils/isApp';
import { getContainerProviderProps } from 'client/utils/providerProps';
import React, { useEffect, useRef } from 'react';
import { type RecProduct } from 'server/services/recommendation';
import CarouselProductCard from './CarouselProductCard';

interface CarouselProps {
  products: RecProduct[];
  'data-testid': string;
  [key: string]: any;
}

export default function Carousel({
  products,
  'data-testid': dataTestId,
  ...props
}: CarouselProps) {
  const container = useRef<HTMLDivElement | null>(null);
  const { rec, provider, placement } = useConfigContext();

  useCriteoLoadEvent(rec?.onLoadBeacon);
  useCriteoViewEvent(container);

  const handleProductClick = async (): Promise<void> => {
    try {
      const { setSession } = await import('client/utils/sessionStorage');
      const prov: string = provider === 'recs2' ? 'constructor' : 'criteo';
      const sessionValue = `${prov}_${placement}`;
      setSession('recs-product', sessionValue);
    } catch (error) {
      console.error(error);
    }
  };

  if (!products.length) return null;

  useEffect(() => {
    (async () => {
      if (await isApp()) {
        createRecsWindowObj(products);
      }
    })();
  }, products);

  return (
    <div ref={container}>
      <FuseCarousel
        {...props}
        {...getContainerProviderProps({ provider, rec })}
        dataTestId={dataTestId}
      >
        {products.map((product) => (
          <CarouselProductCard
            key={product.itemNumber}
            onClick={handleProductClick}
            product={product}
            data-testid="recommendations-product"
          />
        ))}
      </FuseCarousel>
    </div>
  );
}
